import { create } from 'zustand';

export type ModalStoreState = {
  isOpen: boolean;
};

export type ModalStoreActions<T> = {
  close: () => void;
  closeWithoutReset: () => void;
  open: (customData?: T) => void;
  toggle: () => void;
};

export type AnyModalStore = {
  subscribe: (listener: (state: ModalStoreState, previous: ModalStoreState) => void) => () => void;
  getState: () => { close: () => void; isOpen: boolean };
};

const modalStoreRegistry = new Set<AnyModalStore>();
const modalOccupants = new Set<AnyModalStore>();
const closeListeners = new Set<() => void>();

const notifyOnClose = (store: AnyModalStore) => {
  store.subscribe((state, previous) => {
    if (previous.isOpen && !state.isOpen) closeListeners.forEach((listener) => listener());
  });
};

export const createModalStore = <T>(initialData?: T, isOpen = false) => {
  const store = create<ModalStoreActions<T> & ModalStoreState & T>((set) => ({
    ...(initialData || ({} as T)),
    close: () =>
      set((state) => {
        return {
          ...state,
          ...initialData,
          isOpen: false,
        };
      }),
    closeWithoutReset: () =>
      set((state) => {
        return {
          ...state,
          isOpen: false,
        };
      }),
    isOpen,
    open: (customData?: T) =>
      set((state) => {
        if (customData) {
          return {
            ...state,
            ...customData,
            isOpen: true,
          };
        }

        return { ...state, isOpen: true };
      }),
    toggle: () => set((state) => ({ ...state, isOpen: !state.isOpen })),
  }));

  modalStoreRegistry.add(store);
  notifyOnClose(store);

  return store;
};

export const registerModalOccupant = (store: AnyModalStore) => {
  modalOccupants.add(store);
  notifyOnClose(store);
};

export const closeAllModalsExcept = (except: AnyModalStore) => {
  modalStoreRegistry.forEach((store) => {
    if (store === except) {
      return;
    }

    const { close, isOpen } = store.getState();

    if (isOpen) {
      close();
    }
  });
};

export const isAnyModalOpen = (except?: AnyModalStore) =>
  [...modalStoreRegistry, ...modalOccupants].some((store) => store !== except && store.getState().isOpen);

export const subscribeToModalClose = (listener: () => void) => {
  closeListeners.add(listener);

  return () => {
    closeListeners.delete(listener);
  };
};

export const MODAL_CLOSE_SETTLE_DELAY_MS = 600;

export const subscribeToSettledModalClose = (listener: () => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const unsubscribe = subscribeToModalClose(() => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(listener, MODAL_CLOSE_SETTLE_DELAY_MS);
  });

  return () => {
    unsubscribe();
    clearTimeout(timeoutId);
  };
};
